/**
 * DangerZoneTab Component
 *
 * Dangerous administrative actions for user management.
 * Features: delete user, lock/unlock user, reset password.
 */

import { useState } from 'react';
import {
  IconTrash,
  IconLock,
  IconLockOpen,
  IconKey,
  IconAlertTriangle,
} from '@tabler/icons-react';
import {
  Stack,
  Card,
  Group,
  Button,
  Text,
  Divider,
  Modal,
  TextInput,
  PasswordInput,
  Alert,
  Checkbox,
} from '../../../../components/ui';
import { toast } from '../../../../components/ui';
import { useHttpClient } from '@actschurch/ccms-sdk';
import { getApiUrl } from '../../../../config/ccms';

interface DangerZoneTabProps {
  userId: string;
  userEmail: string;
  isLockedOut: boolean;
  lockoutEnabled: boolean;
  onUserDeleted?: () => void;
  onUserLockToggled?: () => void;
  onPasswordReset?: () => void;
}

export function DangerZoneTab({
  userId,
  userEmail,
  isLockedOut,
  lockoutEnabled: _lockoutEnabled,
  onUserDeleted,
  onUserLockToggled,
  onPasswordReset,
}: DangerZoneTabProps) {
  const httpClient = useHttpClient();

  // Delete modal state
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Lock/unlock state
  const [toggling, setToggling] = useState(false);
  const [lockConfirmOpened, setLockConfirmOpened] = useState(false);

  // Password reset modal state
  const [resetModalOpened, setResetModalOpened] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [sendNotification, setSendNotification] = useState(true);
  const [resetting, setResetting] = useState(false);

  /**
   * Handle user deletion
   */
  const handleDelete = async () => {
    if (confirmEmail !== userEmail) {
      toast.error('Confirmation Failed', 'Email does not match');
      return;
    }

    setDeleting(true);
    try {
      const url = getApiUrl('users', `/api/admin/users/${userId}`);

      await toast.promise(
        httpClient.delete(url),
        {
          loading: 'Deleting user...',
          success: 'User has been permanently deleted',
          error: (err: any) => err.message || 'Failed to delete user',
        }
      );

      setDeleteModalOpened(false);
      setConfirmEmail('');
      onUserDeleted?.();
    } catch {
      // error handled by toast.promise
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Handle lock/unlock toggle
   */
  const handleLockToggle = async () => {
    setToggling(true);
    try {
      const url = getApiUrl('users', `/api/admin/users/${userId}/toggle-activation`);

      await toast.promise(
        httpClient.post(url, {
          userId,
          isActive: isLockedOut,
        }),
        {
          loading: isLockedOut ? 'Unlocking user...' : 'Locking user...',
          success: isLockedOut
            ? 'User has been unlocked and can now access the system'
            : 'User has been locked out of the system',
          error: (err: any) => err.message || 'Failed to toggle lock status',
        }
      );

      setLockConfirmOpened(false);
      onUserLockToggled?.();
    } catch {
      // error handled by toast.promise
    } finally {
      setToggling(false);
    }
  };

  /**
   * Handle password reset with new password
   */
  const handlePasswordReset = async () => {
    if (!newPassword) {
      toast.error('Validation Error', 'Please enter a new password');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Validation Error', 'Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Validation Error', 'Passwords do not match');
      return;
    }

    setResetting(true);
    try {
      const url = getApiUrl('users', `/api/admin/users/${userId}/reset-password`);

      await toast.promise(
        httpClient.post(url, {
          userId,
          newPassword,
          confirmPassword,
          sendNotification,
        }),
        {
          loading: 'Resetting password...',
          success: sendNotification
            ? 'Password has been reset and notification sent to the user'
            : 'Password has been reset successfully',
          error: (err: any) => err.message || 'Failed to reset password',
        }
      );

      setResetModalOpened(false);
      setNewPassword('');
      setConfirmPassword('');
      setSendNotification(true);
      onPasswordReset?.();
    } catch {
      // error handled by toast.promise
    } finally {
      setResetting(false);
    }
  };

  const passwordsMatch = newPassword === confirmPassword;
  const passwordValid = newPassword.length >= 8;

  return (
    <Stack gap="lg">
      <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Warning">
        This section contains dangerous actions that cannot be easily undone. Please proceed
        with caution.
      </Alert>

      {/* Lock/Unlock User */}
      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between">
            <Stack gap={4}>
              <Group gap="xs">
                {isLockedOut ? (
                  <IconLock size={20} color="var(--mantine-color-red-6)" />
                ) : (
                  <IconLockOpen size={20} color="var(--mantine-color-green-6)" />
                )}
                <Text size="md" fw={600}>
                  {isLockedOut ? 'Unlock User' : 'Lock User'}
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                {isLockedOut
                  ? 'This user is currently locked out. Unlock to allow them to access the system again.'
                  : 'Lock this user to prevent them from accessing the system. They will not be able to log in.'}
              </Text>
            </Stack>
            <Button
              color={isLockedOut ? 'green' : 'orange'}
              variant="light"
              leftSection={isLockedOut ? <IconLockOpen size={16} /> : <IconLock size={16} />}
              onClick={() => setLockConfirmOpened(true)}
              loading={toggling}
            >
              {isLockedOut ? 'Unlock User' : 'Lock User'}
            </Button>
          </Group>
        </Stack>
      </Card>

      <Divider />

      {/* Reset Password */}
      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between">
            <Stack gap={4}>
              <Group gap="xs">
                <IconKey size={20} color="var(--mantine-color-blue-6)" />
                <Text size="md" fw={600}>
                  Reset Password
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                Set a new password for this user on their behalf
              </Text>
            </Stack>
            <Button
              color="blue"
              variant="light"
              leftSection={<IconKey size={16} />}
              onClick={() => setResetModalOpened(true)}
            >
              Reset Password
            </Button>
          </Group>
        </Stack>
      </Card>

      <Divider />

      {/* Delete User */}
      <Card withBorder style={{ borderColor: 'var(--mantine-color-red-4)' }}>
        <Stack gap="md">
          <Group justify="space-between">
            <Stack gap={4}>
              <Group gap="xs">
                <IconTrash size={20} color="var(--mantine-color-red-6)" />
                <Text size="md" fw={600} c="red">
                  Delete User
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                Permanently delete this user and all associated data. This action cannot be
                undone.
              </Text>
            </Stack>
            <Button
              color="red"
              variant="filled"
              leftSection={<IconTrash size={16} />}
              onClick={() => setDeleteModalOpened(true)}
            >
              Delete User
            </Button>
          </Group>
        </Stack>
      </Card>

      {/* Lock/Unlock Confirmation Modal */}
      <Modal
        opened={lockConfirmOpened}
        onClose={() => setLockConfirmOpened(false)}
        title={isLockedOut ? 'Unlock User' : 'Lock User'}
        centered
      >
        <Stack gap="md">
          <Alert
            color={isLockedOut ? 'blue' : 'orange'}
            icon={isLockedOut ? <IconLockOpen size={16} /> : <IconLock size={16} />}
          >
            {isLockedOut
              ? 'This will unlock the user and allow them to log in again.'
              : 'This will lock the user out of the system. They will not be able to log in until unlocked.'}
          </Alert>

          <Text size="sm">
            Are you sure you want to {isLockedOut ? 'unlock' : 'lock'}{' '}
            <Text component="span" fw={600}>
              {userEmail}
            </Text>
            ?
          </Text>

          <Group justify="flex-end" mt="md">
            <Button
              variant="light"
              onClick={() => setLockConfirmOpened(false)}
            >
              Cancel
            </Button>
            <Button
              color={isLockedOut ? 'green' : 'orange'}
              onClick={handleLockToggle}
              loading={toggling}
              leftSection={isLockedOut ? <IconLockOpen size={16} /> : <IconLock size={16} />}
            >
              {isLockedOut ? 'Unlock User' : 'Lock User'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        opened={resetModalOpened}
        onClose={() => {
          setResetModalOpened(false);
          setNewPassword('');
          setConfirmPassword('');
          setSendNotification(true);
        }}
        title="Reset User Password"
        centered
      >
        <Stack gap="md">
          <Alert color="blue" icon={<IconKey size={16} />}>
            Set a new password for{' '}
            <Text component="span" fw={600}>
              {userEmail}
            </Text>
          </Alert>

          <PasswordInput
            label="New Password"
            placeholder="Enter new password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={newPassword && !passwordValid ? 'Password must be at least 8 characters' : null}
          />

          <PasswordInput
            label="Confirm Password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={confirmPassword && !passwordsMatch ? 'Passwords do not match' : null}
          />

          <Checkbox
            label="Send notification email to user about the password change"
            checked={sendNotification}
            onChange={(e) => setSendNotification(e.currentTarget.checked)}
          />

          <Group justify="flex-end" mt="md">
            <Button
              variant="light"
              onClick={() => {
                setResetModalOpened(false);
                setNewPassword('');
                setConfirmPassword('');
                setSendNotification(true);
              }}
            >
              Cancel
            </Button>
            <Button
              color="blue"
              onClick={handlePasswordReset}
              loading={resetting}
              disabled={!newPassword || !confirmPassword || !passwordsMatch || !passwordValid}
              leftSection={<IconKey size={16} />}
            >
              Reset Password
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={() => {
          setDeleteModalOpened(false);
          setConfirmEmail('');
        }}
        title="Delete User"
        centered
      >
        <Stack gap="md">
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            This action is permanent and cannot be undone. All user data will be deleted.
          </Alert>

          <Text size="sm">
            To confirm deletion, please type the user's email address:{' '}
            <Text component="span" fw={600}>
              {userEmail}
            </Text>
          </Text>

          <TextInput
            label="Confirm Email"
            placeholder={userEmail}
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            error={confirmEmail && confirmEmail !== userEmail ? 'Email does not match' : null}
          />

          <Group justify="flex-end" mt="md">
            <Button
              variant="light"
              onClick={() => {
                setDeleteModalOpened(false);
                setConfirmEmail('');
              }}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDelete}
              loading={deleting}
              disabled={confirmEmail !== userEmail}
              leftSection={<IconTrash size={16} />}
            >
              Delete User
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
